import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Upload } from "lucide-react";
import { CSV_IMPORT_TYPE, type ExchangeRateSnapshot } from "@forgepoint/shared";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SettingsRow } from "@/modules/standardAgent/components/SettingsRow";
import { SectionLabel } from "@/components/settings/SectionLabel";
import { RatesList } from "@/components/settings/RatesList";
import CsvImportSheet from "@/modules/csvImports/CsvImportSheet";
import { fetchApi, apiUrl, getAuthToken } from "@/lib/api";
import { useHasPermission } from "@/store/authStore";
import { Permission } from "@forgepoint/shared";

/**
 * Formats a snapshot's upload date as e.g. "25 Jun 2026".
 * @param date - the snapshot's createdAt timestamp
 * @returns the formatted date label
 */
const formatUploadedOn = (date: Date): string =>
  new Date(date).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

/**
 * Organization settings section for the exchange-rate table. Shows one snapshot
 * at a time, selectable by upload date; the most recent is the active table that
 * quotes pin to, older ones are clearly marked inactive. Launches the shared CSV
 * import flow to upload a new table.
 * @returns The exchange-rates settings section element
 */
export function ExchangeRatesSettings() {
  const [snapshots, setSnapshots] = useState<ExchangeRateSnapshot[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const canEdit = useHasPermission(Permission.SETTINGS_EDIT);

  const [active] = snapshots;
  const selected = snapshots.find((s) => s.id === selectedId) ?? active;
  const isViewingActive = selected?.id === active?.id;

  const loadHistory = useCallback(async () => {
    const response = await fetchApi("exchange-rates/history", "GET");
    if (response.ok && response.data) {
      const { snapshots: all } = response.data as {
        snapshots: ExchangeRateSnapshot[];
      };
      setSnapshots(all);
      setSelectedId(all[0]?.id ?? null);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleExport = useCallback(async () => {
    if (!active) {
      return;
    }
    const authToken = getAuthToken();
    const response = await fetch(`${apiUrl}/exchange-rates/export`, {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    });
    if (!response.ok) {
      toast.error("Failed to export exchange rates.");
      return;
    }
    const updatedOn = new Date(active.createdAt).toISOString().slice(0, 10);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `exchange_rates_${updatedOn}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, [active]);

  return (
    <>
      <SectionLabel title="Exchange Rates" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          <SettingsRow
            label="Exchange Rate Table"
            description={
              canEdit
                ? "Upload a CSV of from,to,rate rows (e.g. USD,CAD,1.36). Each import replaces the active table."
                : "Download the active exchange rate table as a CSV."
            }
          >
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!active}
                onClick={handleExport}
              >
                <Download className="h-4 w-4" />
                Export CSV
              </Button>
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setImportOpen(true)}
                >
                  <Upload className="h-4 w-4" />
                  Import CSV
                </Button>
              )}
            </div>
          </SettingsRow>

          <SettingsRow
            label="Rates"
            description={
              selected
                ? `Uploaded ${formatUploadedOn(selected.createdAt)}.`
                : "No exchange rate table uploaded yet."
            }
            expandedContent={
              isLoading ? (
                <div className="flex justify-center py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : selected ? (
                <RatesList rates={selected.rates} />
              ) : null
            }
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : snapshots.length > 0 ? (
              <>
                <Badge variant={isViewingActive ? "default" : "secondary"}>
                  {isViewingActive ? "Active" : "Inactive"}
                </Badge>
                <Select
                  value={selected?.id}
                  onValueChange={setSelectedId}
                  disabled={snapshots.length < 2}
                >
                  <SelectTrigger className="h-8 w-auto gap-2 whitespace-nowrap">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {active && (
                      <SelectGroup>
                        <SelectLabel>Active</SelectLabel>
                        <SelectItem value={active.id}>
                          {formatUploadedOn(active.createdAt)}
                        </SelectItem>
                      </SelectGroup>
                    )}
                    {snapshots.length > 1 && (
                      <SelectGroup>
                        <SelectLabel>Inactive</SelectLabel>
                        {snapshots.slice(1).map((snap) => (
                          <SelectItem key={snap.id} value={snap.id}>
                            {formatUploadedOn(snap.createdAt)}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
              </>
            ) : (
              <span className="text-sm text-muted-foreground">--</span>
            )}
          </SettingsRow>
        </div>
      </Card>

      <CsvImportSheet
        importType={CSV_IMPORT_TYPE.EXCHANGE_RATES}
        open={importOpen}
        onOpenChange={setImportOpen}
        onImportComplete={loadHistory}
      />
    </>
  );
}
